import React from "react";

import { Shimmer, ShimmerKeyframes } from "./Shimmer";

type Props = {
  columnCount?: number;
  cardsPerColumn?: number;
};

const d = (n: number) => `${n}s`;

const KanbanCardSkeleton = ({ delay }: { delay: number }) => (
  <div className="flex flex-col gap-sm p-md rounded-lg border border-solid border-primary bg-primary">
    <div className="flex items-center justify-between gap-sm">
      <Shimmer className="h-md w-[60px] rounded" delay={d(delay)} />
      <Shimmer className="h-md w-md rounded" delay={d(delay + 0.05)} />
    </div>
    <Shimmer className="h-lg w-full rounded" delay={d(delay + 0.1)} />
    <Shimmer className="h-md w-3/4 rounded" delay={d(delay + 0.15)} />
    <div className="flex items-center justify-between mt-xs">
      <Shimmer className="h-xl w-[70px] rounded-full" delay={d(delay + 0.2)} />
      <div className="flex items-center -space-x-xs">
        <Shimmer
          className="w-2xl h-2xl rounded-full border-2 border-solid border-primary"
          delay={d(delay + 0.25)}
        />
        <Shimmer
          className="w-2xl h-2xl rounded-full border-2 border-solid border-primary"
          delay={d(delay + 0.3)}
        />
      </div>
    </div>
  </div>
);

const KanbanColumnSkeleton = ({
  columnIdx,
  cardsPerColumn,
}: {
  columnIdx: number;
  cardsPerColumn: number;
}) => (
  <div className="flex flex-col gap-md flex-shrink-0 w-[320px] p-md rounded-lg bg-tertiary">
    <div className="flex items-center justify-between gap-sm px-sm">
      <div className="flex items-center gap-sm">
        <Shimmer
          className="w-md h-md rounded-full"
          delay={d(columnIdx * 0.1)}
        />
        <Shimmer
          className="h-md w-[100px] rounded"
          delay={d(columnIdx * 0.1 + 0.05)}
        />
        <Shimmer
          className="h-md w-md rounded-full"
          delay={d(columnIdx * 0.1 + 0.1)}
        />
      </div>
      <Shimmer
        className="h-md w-md rounded"
        delay={d(columnIdx * 0.1 + 0.15)}
      />
    </div>
    <div className="flex flex-col gap-sm">
      {Array.from({ length: cardsPerColumn }).map((_, cardIdx) => (
        <KanbanCardSkeleton
          key={cardIdx}
          delay={columnIdx * 0.1 + cardIdx * 0.08 + 0.2}
        />
      ))}
    </div>
  </div>
);

const WorkOrderBoardSkeleton = ({
  columnCount = 4,
  cardsPerColumn = 3,
}: Props) => (
  <>
    <ShimmerKeyframes />
    <div className="flex gap-md overflow-x-auto p-md">
      {Array.from({ length: columnCount }).map((_, columnIdx) => (
        <KanbanColumnSkeleton
          key={columnIdx}
          columnIdx={columnIdx}
          cardsPerColumn={cardsPerColumn}
        />
      ))}
    </div>
  </>
);

export default WorkOrderBoardSkeleton;
